import { Loader } from "@progress/kendo-react-indicators";
import { useEffect, useState } from "react";
import ContentPlaceholder from "../../components/ContentPlaceholder";
import AddRecordingComment from "../../components/recording-comments/AddRecordingComment";
import RecordingCommentsList from "../../components/recording-comments/RecordingCommentsList";
import useLocale from "../../hooks/useLocale";
import { Dictionary } from "../../types/Dictionary";
import { Comment } from "../../types/recording";
import { RegionSelection } from "./CallDetails";
import "./RecordingComments.scss";
import useSwal from "../../hooks/useSwal";
import { SweetAlertOptions } from "sweetalert2";
import usersService from "../../services/users.service";
import { TinyUser, User } from "../../types/user";
import useMasterData from "../../hooks/useMasterData";
import useAuth from "../../hooks/useAuth";
import useTranslation from "../../hooks/useTranslation";

interface TinyUserSelect extends TinyUser {
  checked?: boolean;
}

interface RecordingCommentsProps {
  comments?: Comment[];
  error?: string;
  recordingId: number;
  regionSelection: RegionSelection;
  recordingDuration: number;
  audioLoaded: boolean;
  disableCommentPlay: boolean;
  shared?: boolean;
  onCommentMouseOver: (commentId: number) => void;
  onCommentMouseOut: () => void;
  onAddComment: (comment: Comment) => void;
  onDeleteComment: (commentId: number) => void;
  onUpdateComment: (comment: Comment) => void;
  platformCallDetailId?: string;
  recordingPath: string;
  baseLicenseId?: number;
  totalCommentsTemp?: number;
  setTotalCommentsTemp: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  highlightedCommentId: number;
}

const RecordingComments: React.FC<RecordingCommentsProps> = ({
  comments,
  error,
  recordingId,
  regionSelection,
  recordingDuration,
  audioLoaded,
  disableCommentPlay,
  shared,
  onCommentMouseOver,
  onCommentMouseOut,
  onAddComment,
  onDeleteComment,
  onUpdateComment,
  platformCallDetailId,
  recordingPath,
  baseLicenseId,
  totalCommentsTemp,
  setTotalCommentsTemp,
  highlightedCommentId,
}) => {
  const trans = useTranslation("RecordingComment");
  const auth = useAuth();
  const master = useMasterData();
  const localeCtx = useLocale();
  const swal = useSwal();
  const [internalUsers, setInternalUsers] = useState<TinyUserSelect[]>([]);

  useEffect(() => {
    if (
      !localeCtx?.selectedLocale?.current.componentTranslations[
        "RecordingComment"
      ]
    ) {
      trans.fetchTranslations("RecordingComment");
    }
  }, [localeCtx?.selectedLocale]);

  useEffect(() => {
    const fetchInternalUsers = async () => {
      try {
        var users: User[] = []; 
        console.log(master?.standardUsers);
        if(master?.standardUsers){
          users = master?.standardUsers.filter((obj) => {
            return obj.loginEnabled == true && obj.isArchived == false;
          });
        } else{
          users = await usersService.fetchUsers();
          master?.setStandardUsers(users);
          users = users.filter((obj) => {
            return obj.loginEnabled == true && obj.isArchived == false;
          });
        }
        var tinyUsers = users.map((user) => {
          let obj: TinyUserSelect = {
            id: user.id,
            loginUserId: user.loginUserId,
            firstName: user.firstName ?? "",
            lastName: user.lastName ?? "",
            email: user.email,
            phoneNo: user.phoneNo,
            image: user.image,
            isArchived: user.isArchived,
            checked: false,
          };
          return obj;
        });
        setInternalUsers(tinyUsers);
      } catch (err) {
        setInternalUsers([]);
      }
    };
    if (!shared || (shared && auth?.accessToken)) {
      fetchInternalUsers();
    }
  }, [shared]);

  return (
    <div className="row p-t-2">
      {!comments && (
        <div className="col-md-7 mb-2">
          <div className="chat-bx h-100">
            <div className="chat-conversation h-100">
              <ContentPlaceholder>
                {!error && <Loader type={"infinite-spinner"} />}
                {error && (
                  <span>
                    <i className="bi bi-exclamation-triangle-fill tx-amber"></i>{" "}
                    {trans.fetchLabelKeyTranslation(
                      "SwtAltAddedFailedText",
                      "Uh Oh! Something went wrong. Please try again..."
                    )}
                  </span>
                )}
              </ContentPlaceholder>
            </div>
          </div>
        </div>
      )}
      {comments && (
        <RecordingCommentsList
          recordingId={recordingId}
          comments={comments}
          onDeleteComment={onDeleteComment}
          onUpdateComment={onUpdateComment}
          audioLoaded={audioLoaded}
          disableCommentPlay={disableCommentPlay}
          onCommentMouseOver={onCommentMouseOver}
          onCommentMouseOut={onCommentMouseOut}
          shared={shared}
          fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
          platformCallDetailId={platformCallDetailId}
          internalUsers={internalUsers}
          recordingPath={recordingPath}
          setTotalCommentsTemp={setTotalCommentsTemp}
          totalCommentsTemp={totalCommentsTemp}
          highlightedCommentId={highlightedCommentId}
        />
      )}
      <AddRecordingComment
        recordingId={recordingId}
        regionSelection={regionSelection}
        recordingDuration={recordingDuration}
        onAddComment={onAddComment}
        shared={shared}
        fetchLabelKeyTranslation={trans.fetchLabelKeyTranslation}
        platformCallDetailId={platformCallDetailId}
        internalUsers={internalUsers}
        recordingPath={recordingPath}
        setTotalCommentsTemp={setTotalCommentsTemp}
        totalCommentsTemp={totalCommentsTemp}
        baseLicenseId={baseLicenseId}
      />
    </div>
  );
};

export default RecordingComments;
